import { Flex, Text } from '@chakra-ui/react';
import { faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, ButtonText, Checkbox, Table } from '@morressier/alembic';
import { ArticleTypeConfigField } from '../helpers/articleTypeConfig.types';
import {
  formatKeyPath,
  getDisplayFieldTypeLabel,
  hasContentLabelKey,
  shouldUseContentLabelMode
} from '../helpers/articleTypeConfig.utils';
import ExpandableObjectCell from './ExpandableObjectCell';

type Props = {
  row: ArticleTypeConfigField;
  componentName?: string;
  showSelectCheckbox: boolean;
  isSelected: boolean;
  isDeleteDisabled?: boolean;
  hideBottomBorder?: boolean;
  onToggleRow?: (checked: boolean) => void;
  onEdit: () => void;
  onDelete?: () => void;
};

const FieldConfigTableRow: React.FC<Props> = ({
  row,
  componentName,
  showSelectCheckbox,
  isSelected,
  isDeleteDisabled = false,
  hideBottomBorder = false,
  onToggleRow,
  onEdit,
  onDelete
}) => {
  const hasContentLabel = hasContentLabelKey(row?.labels);
  const shouldUseContentMode = shouldUseContentLabelMode(row?.labels);
  const validationOmitKeys = ['kind', 'required', 'pattern', 'items'];
  const labelOmitKeys = shouldUseContentMode
    ? ['main', 'subtitle']
    : hasContentLabel
    ? ['content']
    : [];

  if (row.kind === 'authorType') {
    validationOmitKeys.push('min', 'max', 'minLength', 'maxLength');
  }

  const displayedTypeName = getDisplayFieldTypeLabel({
    fieldName: row.field,
    kind: row.kind,
    componentName
  });

  return (
    <Table.Row
      gap={1}
      alignItems={'baseline'}
      textAlign={'left'}
      borderBottom={hideBottomBorder ? 'none' : ''}
    >
      <Table.RowCell width={10} textStyle="textBody" textAlign="left">
        {showSelectCheckbox ? (
          <Checkbox
            id={`field-select-${row.id}`}
            name={`field-select-${row.id}`}
            ariaLabel={`Select ${row.field} row`}
            isChecked={isSelected}
            onChange={event => onToggleRow?.(event.target.checked)}
            disabled={isDeleteDisabled}
          >
            {' '}
          </Checkbox>
        ) : null}
      </Table.RowCell>
      <Table.RowCell flex="1" textStyle="textBody" textAlign="left">
        <Text
          whiteSpace="normal"
          overflowWrap="anywhere"
          textStyle={'headingItem'}
        >
          {formatKeyPath(row.field) || '-'}
        </Text>
      </Table.RowCell>
      <Table.RowCell flex="1" textStyle="textBody" textAlign="left">
        <Text>{displayedTypeName}</Text>
      </Table.RowCell>
      <Table.RowCell flex="1" textStyle="textBody" textAlign="left">
        <Badge size="context" variant={row.required ? 'neutral' : 'attention'}>
          {row.required ? 'Mandatory' : 'Optional'}
        </Badge>
      </Table.RowCell>
      <Table.RowCell flex="1" textStyle="textBody" textAlign="left">
        <ExpandableObjectCell
          value={row?.validations}
          omitKeys={validationOmitKeys}
          modalTitle={`${row.field} validations`}
        />
      </Table.RowCell>
      <Table.RowCell flex="1" textStyle="textBody" textAlign="left">
        <ExpandableObjectCell
          value={row?.labels}
          omitKeys={labelOmitKeys}
          modalTitle={`${row.field} labels`}
        />
      </Table.RowCell>
      <Table.RowCell width={24} textStyle="textBody">
        <Flex gap={2} justifyContent={'flex-end'}>
          <ButtonText
            size="small"
            variant="secondary"
            aria-label={`Edit ${row.field}`}
            onClick={onEdit}
          >
            <FontAwesomeIcon icon={faPen} />
          </ButtonText>
          {onDelete ? (
            <ButtonText
              size="small"
              variant="caution"
              aria-label={`Delete ${row.field}`}
              onClick={onDelete}
              isDisabled={isDeleteDisabled}
            >
              <FontAwesomeIcon icon={faTrash} />
            </ButtonText>
          ) : (
            <ButtonText visibility={'hidden'} size="small">
              {''}
            </ButtonText>
          )}
        </Flex>
      </Table.RowCell>
    </Table.Row>
  );
};

export default FieldConfigTableRow;
